import { isValid as isValidDate, parseISO, differenceInYears } from 'date-fns';
import { MIN_ADULT_DEPENDANT_AGE, MAX_ADULT_DEPENDANT_AGE_31 } from './constants';
import { Selected } from '@nib/phi-constants';
// checks ID if it is from an Object - example 'members.0.firstName', 'members.1.lastName'
export const isIdFromObject = id => id.includes('.');
const formikErrorMessage = (propsId, data, touched) => {
  if (isIdFromObject(propsId) && touched) {
    /*
      formik FieldArray gives us unconventional field IDs
      for example: "members.0.firstName" (at index 0) or "members.1.lastName" (at index 1)
      we then destructure "propsId" using split method
      fields are "firstName", "lastName", 'sex' inside array of objects
    */
    const [id, index, field] = propsId.split('.');
    if (data[id] && data[id][index] && data[id][index][field]) {
      const errMsg = data[id][index][field];
      return errMsg;
    }
  }
  if (data[propsId] && touched) {
    return data[propsId];
  }
  return '';
};
/* eslint-disable complexity */
export const isTouched = props => {
  if (typeof props.validated !== 'undefined') {
    return props.validated;
  }
  const [id, index, field] = props.id.split('.');
  if (props.meta && props.meta.touched) {
    return true;
  }
  if (!isIdFromObject(props.id) && props.form && props.form.touched && props.form.touched[id]) {
    return true;
  }
  if (isIdFromObject(props.id) && props.form && props.form.touched && props.form.touched[id] && props.form.touched[id][index] && props.form.touched[id][index][field]) {
    return true;
  }
  return false;
};
/* eslint-enable complexity */
export const errorMessage = props => {
  if (typeof props.error !== 'undefined') {
    return props.error;
  }
  if (props.form && props.form.errors) {
    return formikErrorMessage(props.id, props.form.errors, isTouched(props));
  }
  if (props.meta && props.meta.touched) {
    return props.meta.error;
  }
  return null;
};
/* eslint-disable complexity */
export const isValid = props => {
  var _a;
  if (typeof props.valid !== 'undefined') {
    return props.valid;
  }
  const [id, index, field] = (_a = props.id) === null || _a === void 0 ? void 0 : _a.split('.');
  // Bypass empty errors{} - formik
  if (props.form && props.form.errors && Object.keys(props.form.errors).length > 0) {
    if (!isIdFromObject(props.id) && props.form.errors && props.form.errors[id]) {
      return false;
    }
    if (isIdFromObject(props.id) && props.form.errors && props.form.errors[id] && props.form.errors[id][index] && props.form.errors[id][index][field]) {
      return false;
    }
  }
  if (props.meta && props.meta.error) {
    return false;
  }
  return true;
};
/* eslint-enable complexity */
export const isFieldOptionalAndEmpty = (optional, field) => {
  if (optional && field) {
    return Boolean(!field.value);
  }
  return false;
};
// DOB needs it's own specific functions due to the 3 fields and one hidden.
const formikDOBErrorMessage = (propsId, errors, fieldName, dateFieldNames, touched) => {
  if (isIdFromObject(propsId)) {
    /*
      formik FieldArray gives us unconventional field IDs
      for example: "dependants.0.childDateOfBirth" (at index 0) or "dependants.1.childDateOfBirth" (at index 1)
      we then destructure "propsId" using split method
      eg fields are "childDateOfBirth", "childDateOfBirthDay", "childDateOfBirthMonth", "childDateOfBirthYear" inside array of objects
    */
    const [id, index, field] = propsId.split('.');
    if (errors[id] && errors[id][index] && errors[id][index][field]) {
      const errMsg = errors[id][index][field];
      return errMsg;
    }
  }
  if (errors[propsId] || errors[dateFieldNames.day] || errors[dateFieldNames.month] || errors[dateFieldNames.year]) {
    if (errors[dateFieldNames.day] && touched[dateFieldNames.day]) {
      return errors[dateFieldNames.day];
    }
    if (errors[dateFieldNames.month] && touched[dateFieldNames.month]) {
      return errors[dateFieldNames.month];
    }
    if (errors[dateFieldNames.year] && touched[dateFieldNames.year]) {
      return errors[dateFieldNames.year];
    }
    if (errors && errors[fieldName] && touched[dateFieldNames.day] && touched[dateFieldNames.month] && touched[dateFieldNames.year]) {
      return errors[fieldName];
    }
  }
};
export const dateErrorMessage = (props, fieldName, dateFieldNames) => {
  if (props.form && props.form.errors) {
    return formikDOBErrorMessage(props.id, props.form.errors, fieldName, dateFieldNames, props.form.touched);
  }
  return null;
};
/* eslint-disable complexity */
export const isDateValid = (props, fieldName, dateFieldNames) => {
  const errors = props.form.errors;
  const touched = props.form.touched;
  if (typeof props.valid !== 'undefined') {
    return props.valid;
  }
  if (errors && Object.keys(errors).length > 0) {
    // not a dependant child
    if (!isIdFromObject(props.id)) {
      /*
        We want to show the dateOfBirth field as invalid if any field is invalid as part of https://jira.nib.com.au/browse/PHISL-2201
        Previously we only would show it invalid if all fields were touched and the hidden field was invalid.
      */
      if (errors && errors[dateFieldNames.day] && touched[dateFieldNames.day]) {
        return false;
      }
      if (errors && errors[dateFieldNames.month] && touched[dateFieldNames.month]) {
        return false;
      }
      if (errors && errors[dateFieldNames.year] && touched[dateFieldNames.year]) {
        return false;
      }
      if (errors && errors[fieldName] && touched[dateFieldNames.day] && touched[dateFieldNames.month] && touched[dateFieldNames.year]) {
        return false;
      }
      if (errors && errors[props.id] && touched[dateFieldNames.day] && touched[dateFieldNames.month] && touched[dateFieldNames.year]) {
        return false;
      }
      return true;
    }
    const [id, index, field] = props.id.split('.');
    // is child dob.
    if (isIdFromObject(props.id) && errors && errors[id] && errors[id][index] && touched && touched[id] && touched[id][index]) {
      const dayField = dateFieldNames.day.split('.')[2];
      const monthField = dateFieldNames.month.split('.')[2];
      const yearField = dateFieldNames.year.split('.')[2];
      // invalidate if any date field eg day, month or year is errored plus touched.
      if (errors[id][index][dayField] && touched[id][index][dayField]) {
        return false;
      }
      if (errors[id][index][monthField] && touched[id][index][monthField]) {
        return false;
      }
      if (errors[id][index][yearField] && touched[id][index][yearField]) {
        return false;
      }
      if (errors[id][index][field] && touched[id][index][dayField] && touched[id][index][monthField] && touched[id][index][yearField]) {
        return false;
      }
    }
  }
  return true;
};
export const leadingZero = value => Number(value).toString().length < 2 ? `0${Number(value)}` : `${value}`;
export const toISODate = value => {
  if (!value) {
    return value;
  }
  const dateSplitToArray = value.split('-');
  if (dateSplitToArray) {
    return `${dateSplitToArray[0]}-${leadingZero(dateSplitToArray[1])}-${leadingZero(dateSplitToArray[2])}`;
  } else {
    return value;
  }
};
// null if no adult dependants, otherwise returns index of first adult dependant
export const hasAdultDependants = function (dependants) {
  let minAdultDependantAge = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MIN_ADULT_DEPENDANT_AGE;
  let maxAdultDependantAge = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : MAX_ADULT_DEPENDANT_AGE_31;
  const today = new Date();
  let index = 0;
  let hasAdultDependant = false;
  for (const dependant of dependants) {
    const isoDateString = toISODate(dependant.childDateOfBirth);
    const dateDob = parseISO(isoDateString);
    if (isValidDate(dateDob)) {
      const yearsDiff = Math.abs(differenceInYears(dateDob, today));
      const isStudentDependant = dependant.isStudentDependant === Selected.yes;
      // This is ONLY available when student dependant max age is 31
      if (!isStudentDependant && yearsDiff >= minAdultDependantAge && yearsDiff < maxAdultDependantAge) {
        hasAdultDependant = true;
        break;
      }
      index++;
    }
  }
  if (hasAdultDependant) return index;
  return null;
};
export const isJson = str => {
  if (str === null) {
    return false;
  }
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};
export const generateNameMask = function () {
  let maxCharLength = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 61;
  return [/[A-Za-z0-9\-']/, ...Array(maxCharLength - 2).fill(/[A-Za-z0-9\s\-']/), /[A-Za-z0-9\-']/];
};